import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API
    from "../../utils/apiCalling";

import {
    config,
} from "../../utils/apiUrl";


/*
|--------------------------------------------------------------------------
| PLATFORM API
|--------------------------------------------------------------------------
*/

const platformApi =
    new API({
        tokenKey:
            "platformAccessToken",
    });


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {

    /*
    |--------------------------------------------------------------------------
    | ASSIGN PLAN
    |--------------------------------------------------------------------------
    */

    assignPlan: {

        data:
            null,

        loading:
            false,

        error:
            null,

        success:
            false,

    },

};


/*
|--------------------------------------------------------------------------
| ASSIGN SUBSCRIPTION PLAN TO TENANT
|--------------------------------------------------------------------------
|
| Super Admin only.
|
| This does NOT:
|
| - override permissions
| - override modules
| - create custom tenant access
|
| Tenant receives exactly the selected plan entitlement.
|
|--------------------------------------------------------------------------
*/

export const assignPlatformTenantSubscriptionPlan =
    createAsyncThunk(
        "platformTenantSubscription/assignPlan",

        async (
            {
                tenantId,

                planId,

                billingCycle,

                assignmentReason = "",
            },
            {
                rejectWithValue,
            }
        ) => {

            try {

                /*
                |--------------------------------------------------------------------------
                | VALIDATION
                |--------------------------------------------------------------------------
                */

                if (
                    !tenantId
                ) {

                    return rejectWithValue({
                        message:
                            "Business id is required.",
                    });

                }


                if (
                    !planId
                ) {

                    return rejectWithValue({
                        message:
                            "Subscription plan is required.",
                    });

                }


                /*
                |--------------------------------------------------------------------------
                | API
                |--------------------------------------------------------------------------
                */

                const response =
                    await platformApi.post(
                        config
                            .assignTenantSubscriptionPlan(
                                tenantId
                            ),

                        {
                            planId,

                            billingCycle,

                            assignmentReason,
                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | API FAILURE
                |--------------------------------------------------------------------------
                */

                if (
                    !response?.success
                ) {

                    return rejectWithValue(
                        response ||
                        {
                            message:
                                "Unable to assign subscription plan.",
                        }
                    );

                }


                /*
                |--------------------------------------------------------------------------
                | SUCCESS
                |--------------------------------------------------------------------------
                */

                return response;

            } catch (
                error
            ) {

                return rejectWithValue({

                    message:
                        error?.response
                            ?.data
                            ?.message ||
                        error?.message ||
                        "Unable to assign subscription plan.",

                });

            }

        }
    );


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const platformTenantSubscriptionSlice =
    createSlice({

        name:
            "platformTenantSubscription",

        initialState,

        reducers: {

            /*
            |--------------------------------------------------------------------------
            | RESET ASSIGNMENT STATE
            |--------------------------------------------------------------------------
            */

            clearPlatformTenantSubscriptionAssignment:
                (
                    state
                ) => {

                    state.assignPlan = {

                        data:
                            null,

                        loading:
                            false,

                        error:
                            null,

                        success:
                            false,

                    };

                },

        },

        extraReducers:
            (
                builder
            ) => {

                /*
                |--------------------------------------------------------------------------
                | ASSIGN PLAN
                |--------------------------------------------------------------------------
                */

                builder

                    /*
                    |--------------------------------------------------------------------------
                    | PENDING
                    |--------------------------------------------------------------------------
                    */

                    .addCase(
                        assignPlatformTenantSubscriptionPlan
                            .pending,

                        (
                            state
                        ) => {

                            state
                                .assignPlan
                                .loading =
                                true;

                            state
                                .assignPlan
                                .error =
                                null;

                            state
                                .assignPlan
                                .success =
                                false;

                        }
                    )


                    /*
                    |--------------------------------------------------------------------------
                    | SUCCESS
                    |--------------------------------------------------------------------------
                    */

                    .addCase(
                        assignPlatformTenantSubscriptionPlan
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state
                                .assignPlan
                                .loading =
                                false;

                            state
                                .assignPlan
                                .data =
                                action.payload
                                    ?.data
                                    ?.subscription ||
                                action.payload
                                    ?.data ||
                                null;

                            state
                                .assignPlan
                                .error =
                                null;

                            state
                                .assignPlan
                                .success =
                                true;

                        }
                    )


                    /*
                    |--------------------------------------------------------------------------
                    | FAILURE
                    |--------------------------------------------------------------------------
                    */

                    .addCase(
                        assignPlatformTenantSubscriptionPlan
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state
                                .assignPlan
                                .loading =
                                false;

                            state
                                .assignPlan
                                .success =
                                false;

                            state
                                .assignPlan
                                .error =
                                action.payload ||
                                {
                                    message:
                                        "Unable to assign subscription plan.",
                                };

                        }
                    );

            },

    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {

    clearPlatformTenantSubscriptionAssignment,

} =
    platformTenantSubscriptionSlice
        .actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const selectPlatformTenantSubscriptionAssignment =
    (
        state
    ) =>
        state
            .platformTenantSubscription
            ?.assignPlan ||
        initialState.assignPlan;


export const selectPlatformTenantSubscriptionAssignLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformTenantSubscription
                ?.assignPlan
                ?.loading
        );


export const selectPlatformTenantSubscriptionAssignError =
    (
        state
    ) =>
        state
            .platformTenantSubscription
            ?.assignPlan
            ?.error ||
        null;


export const selectPlatformTenantSubscriptionAssignSuccess =
    (
        state
    ) =>
        Boolean(
            state
                .platformTenantSubscription
                ?.assignPlan
                ?.success
        );


/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default
    platformTenantSubscriptionSlice
        .reducer;